import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertTriangle } from "lucide-react";
import PageHead from "../components/PageHead";

export default function LoginPage() {
  const { user, login, register, loading } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    if (!res.success) setError(res.error);
    setSubmitting(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await register(email, password, name);
    if (!res.success) setError(res.error);
    setSubmitting(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--kyra-bg)" }}><p className="text-sm" style={{ color: "var(--kyra-mu)" }}>Chargement...</p></div>;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <>
    <PageHead title="Connexion" description="Connectez-vous a votre espace KYRA." />
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--kyra-bg)" }}>
      <div className="w-full max-w-[420px]">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="no-underline inline-block" data-testid="login-logo">
            <span style={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 900, fontSize: 28, letterSpacing: 4, color: "var(--kyra-gr)" }}>
              KYR<span style={{ color: "var(--kyra-rd)", fontStyle: "italic" }}>A</span>
            </span>
          </Link>
          <p className="text-sm font-light mt-2" style={{ color: "var(--kyra-mu)" }}>{t("login.subtitle")}</p>
        </div>

        <div className="bg-white border rounded-lg p-8" style={{ borderColor: "var(--kyra-li)", boxShadow: "0 2px 24px rgba(0,0,0,0.04)" }}>
          <Tabs value={tab} onValueChange={(v) => { setTab(v); setError(""); }} className="w-full">
            <TabsList className="w-full mb-6" style={{ background: "var(--kyra-bg)" }}>
              <TabsTrigger data-testid="tab-login" value="login" className="flex-1 text-xs">{t("login.tabLogin")}</TabsTrigger>
              <TabsTrigger data-testid="tab-register" value="register" className="flex-1 text-xs">{t("login.tabRegister")}</TabsTrigger>
            </TabsList>

            {error && (
              <div data-testid="auth-error" className="flex items-center gap-2 p-3 rounded-md mb-4 text-xs" style={{ background: "rgba(176,48,37,0.08)", color: "var(--kyra-rd)" }}>
                <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
              </div>
            )}

            <TabsContent value="login">
              <form onSubmit={handleLogin} data-testid="login-form">
                <div className="mb-4">
                  <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>{t("login.email")}</Label>
                  <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("login.phEmail")} className="bg-[var(--kyra-bg)] border-[var(--kyra-li)] focus:border-[var(--kyra-gr)] focus:bg-white" />
                </div>
                <div className="mb-6">
                  <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>{t("login.password")}</Label>
                  <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("login.phPassword")} className="bg-[var(--kyra-bg)] border-[var(--kyra-li)] focus:border-[var(--kyra-gr)] focus:bg-white" />
                </div>
                <button data-testid="login-submit" type="submit" disabled={submitting} className="w-full text-white text-sm font-medium py-3 rounded-md transition-colors cursor-pointer disabled:opacity-50" style={{ background: "var(--kyra-gr)", border: "none" }}>
                  {submitting ? t("login.loginBtnLoading") : t("login.loginBtn")}
                </button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} data-testid="register-form">
                <div className="mb-4">
                  <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>Nom complet</Label>
                  <Input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jean Dupont" className="bg-[var(--kyra-bg)] border-[var(--kyra-li)] focus:border-[var(--kyra-gr)] focus:bg-white" />
                </div>
                <div className="mb-4">
                  <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>Email</Label>
                  <Input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="j.dupont@enseigne.fr" className="bg-[var(--kyra-bg)] border-[var(--kyra-li)] focus:border-[var(--kyra-gr)] focus:bg-white" />
                </div>
                <div className="mb-6">
                  <Label className="text-[11px] font-medium tracking-wider uppercase mb-1.5 block" style={{ color: "var(--kyra-mu)" }}>Mot de passe (min 6 caracteres)</Label>
                  <Input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe securise" className="bg-[var(--kyra-bg)] border-[var(--kyra-li)] focus:border-[var(--kyra-gr)] focus:bg-white" />
                </div>
                <button data-testid="register-submit" type="submit" disabled={submitting} className="w-full text-white text-sm font-medium py-3 rounded-md transition-colors cursor-pointer disabled:opacity-50" style={{ background: "var(--kyra-gr)", border: "none" }}>
                  {submitting ? t("login.registerBtnLoading") : t("login.registerBtn")}
                </button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <p className="text-center text-[11px] font-light mt-4" style={{ color: "var(--kyra-mu)" }}>
          <Link to="/" className="no-underline hover:opacity-70" style={{ color: "var(--kyra-gr)" }}>{t("login.backToSite")}</Link>
        </p>
      </div>
    </div>
    </>
  );
}
